import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSecurityDepositDetails } from "../../redux/customerSlice";
import {
  Box,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
  Badge,
  Image,
  HStack,
  Divider,
  Icon,
  Avatar,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import ImageModal from "../../components/ImageModal";
import {
  FiUser,
  FiBriefcase,
  FiMapPin,
  FiCreditCard,
  FiCalendar,
  FiPhone,
  FiMail,
  FiHash,
  FiInfo,
} from "react-icons/fi";

// ─── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = "blue.500" }) => (
  <Flex align="center" gap={3} mb={5}>
    <Box
      w="36px"
      h="36px"
      borderRadius="10px"
      bg={`${color.split(".")[0]}.50`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} color={color} boxSize={4} />
    </Box>
    <Text fontWeight="700" fontSize="md" color="gray.700">
      {title}
    </Text>
  </Flex>
);

// ─── Info Field ────────────────────────────────────────────────────────────────
const InfoField = ({ label, value, highlight }) => (
  <Box>
    <Text fontSize="11px" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.6px" mb="3px">
      {label}
    </Text>
    <Text
      fontSize="sm"
      fontWeight={highlight ? "700" : "500"}
      color={highlight ? "blue.600" : "gray.800"}
      wordBreak="break-word"
    >
      {value || "N/A"}
    </Text>
  </Box>
);

// ─── Section Card ──────────────────────────────────────────────────────────────
const SectionCard = ({ children }) => (
  <Card
    bg="#fff"
    borderRadius="14px"
    boxShadow="0 2px 10px rgba(0,0,0,0.06)"
    border="1.5px solid"
    borderColor="gray.100"
    p={6}
  >
    {children}
  </Card>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const CustomerDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { customerDetails, detailsLoading } = useSelector((state) => state.customers);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");

  const { customerId, page, rows, initialValue } = location.state || {};

  useEffect(() => {
    if (customerId) {
      dispatch(fetchSecurityDepositDetails({ userid: customerId, id: customerId }));
    }
  }, [dispatch, customerId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const data = customerDetails?.data?.[0] || {};

  // Avatar color
  const avatarBg = "#007bc9";

  return (
    <>
      {/* ── Top Bar ─────────────────────────────────────── */}
      <Card
        display="flex"
        flexDirection="row"
        alignItems="center"
        justify="space-between"
        paddingInline={5}
        paddingBlock={3}
        boxShadow="sm"
        borderRadius="12px"
        mb={1}
      >
        <Flex align="center" gap={3}>
          <Box
            w="38px"
            h="38px"
            borderRadius="10px"
            bg="blue.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiUser} color="blue.500" boxSize={5} />
          </Box>
          <Box>
            <Heading fontSize="lg" color="gray.800">
              Customer Details
            </Heading>
            {customerId && (
              <Text fontSize="xs" color="gray.400">
                Customer ID: #{customerId}
              </Text>
            )}
          </Box>
        </Flex>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title="Back"
          height="34px"
          b_color="#374151"
          onClick={() =>
            navigate("/customer-list", {
              state: { page: page || 1, rows: rows || 5, initialValue: initialValue || 1 },
            })
          }
        />
      </Card>

      {/* ── Loading Skeleton ────────────────────────────── */}
      {detailsLoading ? (
        <VStack spacing={4} align="stretch" mt={4}>
          <SectionCard>
            <Flex gap={4} align="center" mb={6}>
              <SkeletonCircle size="16" />
              <Box flex={1}>
                <Skeleton h="18px" mb={2} w="50%" />
                <Skeleton h="14px" w="30%" />
              </Box>
            </Flex>
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              {[...Array(6)].map((_, i) => (
                <Box key={i}>
                  <Skeleton h="10px" mb={2} w="40%" />
                  <Skeleton h="14px" w="70%" />
                </Box>
              ))}
            </SimpleGrid>
          </SectionCard>
          <SectionCard>
            <Skeleton h="16px" mb={5} w="25%" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              {[...Array(6)].map((_, i) => (
                <Box key={i}>
                  <Skeleton h="10px" mb={2} w="40%" />
                  <Skeleton h="14px" w="70%" />
                </Box>
              ))}
            </SimpleGrid>
          </SectionCard>
        </VStack>
      ) : (
        <VStack spacing={4} align="stretch" mt={4}>

          {/* ── Profile Banner ─────────────────────────────── */}
          <SectionCard>
            <Flex align="center" gap={5} flexWrap="wrap">
              <Avatar
                name={data?.name}
                bg={avatarBg}
                color="#fff"
                size="xl"
                fontWeight="bold"
                fontSize="2xl"
              />
              <Box flex={1} minW="180px">
                <Text fontWeight="800" fontSize="xl" color="gray.800" mb={1}>
                  {data?.name || "N/A"}
                </Text>
                <Flex gap={2} flexWrap="wrap" align="center">
                  {data?.mobile && (
                    <Flex align="center" gap={1}>
                      <Icon as={FiPhone} color="blue.400" boxSize={3} />
                      <Text fontSize="sm" color="gray.500">{data.mobile}</Text>
                    </Flex>
                  )}
                  {data?.email && (
                    <Flex align="center" gap={1}>
                      <Icon as={FiMail} color="green.400" boxSize={3} />
                      <Text fontSize="sm" color="gray.500">{data.email}</Text>
                    </Flex>
                  )}
                </Flex>
                <Flex gap={2} mt={3} flexWrap="wrap">
                  <Badge
                    colorScheme="blue"
                    borderRadius="8px"
                    px={3}
                    py={1}
                    fontSize="xs"
                    fontWeight="600"
                  >
                    ID: #{data?.id || customerId}
                  </Badge>
                  {data?.userid && (
                    <Badge colorScheme="purple" borderRadius="8px" px={3} py={1} fontSize="xs" fontWeight="600">
                      User ID: {data.userid}
                    </Badge>
                  )}
                  <Badge
                    colorScheme={data?.status === "1" ? "green" : "red"}
                    borderRadius="8px"
                    px={3}
                    py={1}
                    fontSize="xs"
                    fontWeight="600"
                  >
                    {data?.status === "1" ? "Active" : "Inactive"}
                  </Badge>
                  {data?.type && (
                    <Badge colorScheme="gray" borderRadius="8px" px={3} py={1} fontSize="xs" fontWeight="600">
                      {data.type}
                    </Badge>
                  )}
                </Flex>
              </Box>
            </Flex>
          </SectionCard>

          {/* ── Basic Details ──────────────────────────────── */}
          <SectionCard>
            <SectionHeader icon={FiUser} title="Basic Information" color="blue.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={6}>
              <InfoField label="Full Name" value={data?.name} />
              <InfoField label="Mobile Number" value={data?.mobile} />
              <InfoField label="Email Address" value={data?.email} />
              <InfoField label="Secondary Email" value={data?.email2} />
              <InfoField label="Customer Type" value={data?.type} />
              <InfoField
                label="Account Status"
                value={data?.status === "1" ? "Active" : "Inactive"}
              />
            </SimpleGrid>
          </SectionCard>

          {/* ── Business Details ───────────────────────────── */}
          <SectionCard>
            <SectionHeader icon={FiBriefcase} title="Business Details" color="purple.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={6}>
              <InfoField label="Business Name" value={data?.business_name} />
              <InfoField label="Trade Name" value={data?.tradename} />
              <InfoField label="Legal Name" value={data?.legalname} />
              <InfoField label="GST Registered" value={data?.is_gst_reg} />
              <InfoField label="GST Number" value={data?.gst} highlight />
              <InfoField label="PAN Number" value={data?.pan} highlight />
            </SimpleGrid>
          </SectionCard>

          {/* ── Address Information ────────────────────────── */}
          <SectionCard>
            <SectionHeader icon={FiMapPin} title="Address Information" color="orange.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={6}>
              <InfoField label="Address" value={data?.address} />
              <InfoField label="Pincode" value={data?.pincode} />
              <InfoField label="Area" value={data?.pincode_area} />
              <InfoField label="District" value={data?.district} />
              <InfoField label="State" value={data?.state} />
            </SimpleGrid>
          </SectionCard>

          {/* ── Bank Details ───────────────────────────────── */}
          {data?.bank && data.bank.length > 0 && (
            <Box>
              <Flex align="center" gap={2} mb={3}>
                <Icon as={FiCreditCard} color="teal.500" boxSize={4} />
                <Text fontWeight="700" fontSize="md" color="gray.700">
                  Bank Accounts
                  <Badge ml={2} colorScheme="teal" borderRadius="8px" px={2} fontSize="xs">
                    {data.bank.length}
                  </Badge>
                </Text>
              </Flex>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                {data.bank.map((bank, index) => (
                  <Card
                    key={index}
                    borderRadius="14px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    boxShadow="0 2px 8px rgba(0,0,0,0.05)"
                    overflow="hidden"
                  >
                    {/* Bank card top strip */}
                    <Box h="3px" bg="teal.400" />
                    <Box p={5}>
                      <Flex justify="space-between" align="center" mb={4}>
                        <Flex align="center" gap={2}>
                          <Box
                            w="32px"
                            h="32px"
                            borderRadius="8px"
                            bg="teal.50"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Icon as={FiCreditCard} color="teal.500" boxSize={4} />
                          </Box>
                          <Text fontWeight="700" fontSize="sm" color="gray.700">
                            {bank.bankname || `Account #${index + 1}`}
                          </Text>
                        </Flex>
                        {bank.status === "1" && (
                          <Badge colorScheme="green" borderRadius="8px" px={2} fontSize="xs">
                            Primary
                          </Badge>
                        )}
                      </Flex>

                      <SimpleGrid columns={2} spacing={4}>
                        <InfoField label="Account Holder" value={bank.name} />
                        <InfoField label="Account Number" value={bank.accountno} />
                        <InfoField label="Account Type" value={bank.ac_type} />
                        <InfoField label="IFSC Code" value={bank.ifsc} />
                        <InfoField label="Branch" value={bank.bank_branch} />
                        {bank.upi_id && <InfoField label="UPI ID" value={bank.upi_id} />}
                      </SimpleGrid>

                      {bank.createdat && (
                        <Flex align="center" gap={1} mt={3} pt={3} borderTop="1px solid" borderColor="gray.100">
                          <Icon as={FiCalendar} color="gray.400" boxSize={3} />
                          <Text fontSize="xs" color="gray.400">
                            Added: {formatDate(bank.createdat)}
                          </Text>
                        </Flex>
                      )}

                      {/* QR Code */}
                      {bank.qr_code && (
                        <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.100">
                          <Flex align="center" justify="space-between" mb={3}>
                            <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">
                              UPI QR Code
                            </Text>
                            <CustomButton
                              title="View QR"
                              height="28px"
                              onClick={() => {
                                setUrl(bank.qr_code);
                                onOpen();
                              }}
                            />
                          </Flex>
                          <Box
                            border="1.5px dashed"
                            borderColor="teal.200"
                            p={3}
                            borderRadius="10px"
                            textAlign="center"
                            bg="teal.50"
                          >
                            <Image
                              src={bank.qr_code}
                              alt="Bank QR Code"
                              maxW="100px"
                              maxH="100px"
                              mx="auto"
                              borderRadius="6px"
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Card>
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* ── Timeline Information ───────────────────────── */}
          {(data?.datetime || data?.created_at || data?.payment_date || data?.updated_at) && (
            <SectionCard>
              <SectionHeader icon={FiCalendar} title="Timeline Information" color="teal.500" />
              <SimpleGrid columns={[1, 2, 3]} spacing={6}>
                {(data?.datetime || data?.created_at) && (
                  <InfoField
                    label="Created On"
                    value={formatDate(data?.datetime || data?.created_at)}
                  />
                )}
                {data?.payment_date && (
                  <InfoField label="Payment Date" value={formatDate(data.payment_date)} />
                )}
                {data?.updated_at && (
                  <InfoField label="Last Updated" value={formatDate(data.updated_at)} />
                )}
              </SimpleGrid>
            </SectionCard>
          )}

          {/* ── Payment Proof ──────────────────────────────── */}
          {data?.payment_proof && (
            <SectionCard>
              <SectionHeader icon={FiInfo} title="Payment Proof" color="orange.500" />
              <HStack spacing={4} align="flex-start">
                <Box
                  border="1.5px dashed"
                  borderColor="orange.200"
                  p={3}
                  borderRadius="10px"
                  bg="orange.50"
                >
                  <Image
                    src={data.payment_proof}
                    alt="Payment Proof"
                    maxW="120px"
                    maxH="120px"
                    borderRadius="8px"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={3}>
                    Tap below to view the full payment proof image.
                  </Text>
                  <CustomButton
                    title="View Full Image"
                    height="36px"
                    onClick={() => {
                      setUrl(data.payment_proof);
                      onOpen();
                    }}
                  />
                </Box>
              </HStack>
            </SectionCard>
          )}

          {/* ── Additional Details ─────────────────────────── */}
          {(data?.remarks || data?.notes || data?.description) && (
            <SectionCard>
              <SectionHeader icon={FiInfo} title="Additional Details" color="gray.500" />
              <VStack align="stretch" spacing={4}>
                {data?.remarks && <InfoField label="Remarks" value={data.remarks} />}
                {data?.notes && <InfoField label="Notes" value={data.notes} />}
                {data?.description && <InfoField label="Description" value={data.description} />}
              </VStack>
            </SectionCard>
          )}

          {/* ── No Data Message ────────────────────────────── */}
          {(!data || Object.keys(data).length === 0) && (
            <SectionCard>
              <Box p={8} textAlign="center" color="gray.400">
                <Icon as={FiUser} boxSize={12} mb={4} color="gray.200" />
                <Text fontSize="lg" fontWeight="600" color="gray.500" mb={1}>
                  No details available
                </Text>
                <Text fontSize="sm">
                  Customer ID: {customerId}
                </Text>
              </Box>
            </SectionCard>
          )}

        </VStack>
      )}

      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default CustomerDetail;